/**
 * Embed mode — when Xoro is loaded inside another shell (e.g. Loom),
 * the embedding host appends `?embedded=1` to the iframe src and may
 * also pass `?theme=light|dark`. The host can also send postMessage
 * events at runtime to switch theme without a full reload.
 *
 * Module-level resolution is fine because the host either embeds or
 * doesn't — it doesn't change at runtime within a session.
 */

function detect(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('embedded') === '1') return true;
  } catch { /* noop */ }
  // also treat being inside a different-origin iframe as embedded
  try {
    if (window.self !== window.top) return true;
  } catch {
    // cross-origin frame access throws — that itself means embedded
    return true;
  }
  return false;
}

export const isEmbedded = detect();

type Theme = 'light' | 'dark';

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
}

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('theme');
    if (t === 'dark' || t === 'light') return t;
  } catch { /* noop */ }
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

/** Initialise theme from URL/system pref + listen for parent postMessage updates. */
export function initThemeBridge(): void {
  if (typeof window === 'undefined') return;
  applyTheme(readInitialTheme());
  window.addEventListener('message', (e) => {
    const data = e.data;
    if (!data || typeof data !== 'object') return;
    if (data.type === 'xoro:theme' && (data.theme === 'dark' || data.theme === 'light')) {
      applyTheme(data.theme);
    }
  });
}
